'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';

import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { PriceOfferButton } from '../price-offer/price-offer-button';
import { Button } from '../ui/button';
import { LuArrowUpRight, LuX } from 'react-icons/lu';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: { type: 'page'; name: string; path?: string }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn(
            'flex h-6 w-6 items-center justify-center',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          <div className="flex w-full flex-col items-start  justify-start gap-[3px]">
            <div className="h-[2px] w-[17.5px] rounded-full bg-black" />
            <div className="h-[2px] w-[11.5px] rounded-full bg-black" />
          </div>
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          className={cn(
            'flex w-full max-w-[321px] flex-col items-start justify-start gap-10 overflow-scroll rounded-r-[16px] border-none px-5 pb-10 pt-0 shadow-none',
            'ea-mobile-sidebar__content',
            'hide-scroll-bar',
            props.className?.content,
          )}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex items-center justify-start gap-3 py-5">
            <SheetClose aria-label="Zavrieť menu">
              <LuX size={24} className="text-black" />
            </SheetClose>
            <Image
              src={generalLayout.header?.logo.url || ''}
              alt={resolveImageAlt(
                generalLayout?.header?.logo,
                generalLayout?.contact?.company || '',
              )}
              width={0}
              height={0}
              className="h-8 w-auto object-contain object-center"
            />
          </div>
          <ResponsiveDialogProvider>
            <PriceOfferButton title="Cenová ponuka" variant={'dark'} className="w-full" />
          </ResponsiveDialogProvider>
          <Separator className="h-[1px] w-full bg-black/15" />
          {!!props.menu && (
            <div className="flex w-full flex-col items-start justify-start gap-6">
              {props.menu?.map((item, index) => {
                const Comp = item.path
                  ? isRelativeHref(getSafeHref(item.path))
                    ? Link
                    : 'a'
                  : 'span';
                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={item.path ? getSafeHref(item.path) : (undefined as any)}
                      className="flex w-full"
                      prefetch={false}
                    >
                      <div className={cn('text-2xl font-normal text-black')}>{item.name}</div>
                    </Comp>
                  </SheetClose>
                );
              })}
            </div>
          )}
          <Separator className="h-[1px] w-full bg-black/15" />
          <div className="flex w-full flex-col items-start justify-start gap-3">
            <Button asChild variant={'underlined'} size={'custom'}>
              <Link prefetchOnHover prefetch={false} href={'#'} target="_blank">
                <p className="text-base font-bold">
                  {generalLayout.contact?.address}
                  <br></br>
                  {generalLayout.contact?.postcode} {generalLayout.contact?.city}
                </p>
                <LuArrowUpRight size={20} />
              </Link>
            </Button>
            <Button asChild variant={'underlined'} size={'custom'} className="text-base">
              <Link
                prefetchOnHover
                prefetch={false}
                href={`tel:${generalLayout.contact?.phone}`}
                target="_blank"
              >
                {generalLayout.contact?.phone}
                <LuArrowUpRight size={20} />
              </Link>
            </Button>
            <Button asChild variant={'underlined'} size={'custom'} className="text-base">
              <Link
                prefetchOnHover
                prefetch={false}
                href={`mailto:${generalLayout.contact?.email}`}
                target="_blank"
              >
                {generalLayout.contact?.email}
                <LuArrowUpRight size={20} />
              </Link>
            </Button>
          </div>
          <SocialIcons
            className={{
              button:
                'h-6 w-6 border-none bg-transparent p-0 text-black shadow-none hover:bg-transparent',
              wrapper: 'flex w-full items-center justify-start gap-4',
              icon: 'h-6 w-6 fill-black stroke-black text-black',
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
